// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — entry point (lot B.9 — découpage du monstre)
// Migration S4.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Modale d'édition / création d'une action. Découpée en 3 sous-fichiers
// depuis le lot B.9 (mai 2026) :
//   - index.ts (ici) : showEditActionModal + _wireActionModal (origin picker)
//   - raci-picker.ts : showRaciPicker + chips (R/A/C/I)
//   - notes.ts : _loadNotes / _wireAddNoteButton / _wireNewActionNotes
//   - link-pickers.ts : pickers Projets / Comités / Organisation / Plans + repli
//
// Bare refs utilisées (résolues via core-globals.d.ts) :
//   esc, t, toast, openWideModal, closeModal, closeModalSafe, userLabel,
//   confirmStackModal, safeWarn, currentUserId, sevColor + window.api

import { showRaciPicker, _removeRaciChip, _getRaciIds } from './raci-picker.js';
import { _wireAddNoteButton, _loadNotes, _wireNewActionNotes } from './notes.js';
import {
  computeDefaultStatusId,
  buildOriginalSnapshot,
  buildActionPayload,
  changedActionFields,
  requiredSaveConfirmations,
  shouldBlockForAutoNote,
  type ActionEditData,
  type ActionFormRawInput,
} from '../../domain/action-form.js';
import {
  renderOriginsHtml,
  buildApproverOptions,
  buildOwnerOptions,
  buildStatusOptions,
  renderRaciBlock,
  renderEditorCtas,
  renderEditorTopZone,
} from './editor-fields.js';
import { wireExternalLinkField } from './external-link.js';
import { wireLinkPickers, type LinkPickersSelection, type ProjectOption } from './link-pickers.js';

export interface ActionUser {
  id: number;
  trigram?: string | null;
  name: string;
  is_team?: boolean;
}

export interface ActionStatusOption {
  id: number;
  name: string;
  position: number;
}

interface ShowEditActionModalOptions {
  users?: ActionUser[];
  statuses?: ActionStatusOption[];
  projects?: ProjectOption[];
  onSaved?: (action: unknown, pendingNotes?: string[], notify?: boolean) => void;
  // Cancel callback (RefactoUX/Actions 2026-05-20) — appelé quand l'utilisateur
  // clique "Annuler" dans la sticky-bottom de l'éditeur. Le wrapper modale le
  // câble sur closeModalSafe ; pages.action sur la navigation retour.
  onCancel?: () => void;
  // Contenu HTML brut injecté EN HAUT du bloc sticky-top, au-dessus de la
  // ligne Code/Titre/Priorité. Permet à `pages.action` d'y poser son breadcrumb
  // pour que tout l'en-tête (breadcrumb + code/titre/priorité) reste sticky
  // ensemble pendant un scroll. La modale n'utilise pas cette option.
  stickyTopPrefixHtml?: string;
  prefill?: Partial<ActionEditData>;
  // Replie par défaut les blocs Projets / Comités / Organisation / Plans à
  // l'ouverture (demande UX 2026-07-03) — utilisé par `pages.action` (pleine
  // page) pour désencombrer. La modale d'édition rapide les laisse dépliés.
  collapseLinkSections?: boolean;
  /** Ouvre l'éditeur déjà « modifié » : « Enregistrer » proposé d'emblée, sans
   *  attendre une saisie. Pour les créations pré-remplies (action standard d'un
   *  contrôle de réunion, duplication) — tout est en place, exiger un geste de
   *  plus ne ferait que ralentir. */
  startDirty?: boolean;
}

// API retournée par `renderActionEditor` : permet à l'hôte (modale OU page)
// de déclencher la sauvegarde depuis son propre bouton. `save(notify)` retourne
// `true` si la sauvegarde a réussi (l'hôte doit alors fermer/rediriger),
// `false` si l'utilisateur a annulé une confirmation ou si l'API a échoué
// (l'hôte garde son chrome ouvert).
export interface RenderActionEditorApi {
  save: (notify?: boolean) => Promise<boolean>;
}

declare global {
  interface Window {
    showEditActionModal: typeof showEditActionModal;
    renderActionEditor: typeof renderActionEditor;
  }
}

// ── Wrapper modale ────────────────────────────────────────────────────────
// Conserve la signature historique : 8 callers (page-actions, page-project,
// page-notifications, page-meeting-wizard, page-bascule, page-postits,
// page-import-actions-wizard, etc.) appellent showEditActionModal(action, opts)
// en bare ref via window. RefactoUX/Actions (mai 2026) : l'éditeur lui-même
// vit dans `renderActionEditor` et accepte n'importe quel host DOM ; ce
// wrapper crée un host dans openWideModal et y délègue tout le rendu.
export function showEditActionModal(
  action: ActionEditData,
  options?: ShowEditActionModalOptions,
): void {
  const opts = options || {};
  const isNew = !action.id;
  const title = isNew ? t('actionModal.title.new') : t('actionModal.title.edit', { id: action.id });

  // openWideModal réclame un onConfirm pour son bouton footer, mais le footer
  // est masqué juste après (l'éditeur expose Annuler/Enregistrer/Sans notif
  // dans sa propre sticky-bottom). onConfirm ne sera jamais déclenché, on
  // passe un no-op.
  openWideModal(title, '<div id="ea-editor-host"></div>', async () => {
    /* footer masqué — voir setTimeout ci-dessous */
  });

  setTimeout(() => {
    const host = document.getElementById('ea-editor-host');
    if (!host) return;
    renderActionEditor(host, action, {
      ...opts,
      // Cancel sur la sticky-bottom → ferme la modale (avec garde anti-perte
      // si modifications non sauvegardées, identique à la croix).
      onCancel: () => void closeModalSafe(),
      // Sauvegarde réussie → ferme la modale puis délègue à l'onSaved du caller.
      onSaved: (saved, pendingNotes, notify) => {
        closeModal();
        opts.onSaved?.(saved, pendingNotes, notify);
      },
    });

    // Masque le footer de la modale (croix en header reste pour fermer).
    const footer = document.querySelector('#modal-overlay .modal-footer');
    if (footer) (footer as HTMLElement).style.display = 'none';
  }, 0);
}

// ── Éditeur d'action (réutilisable modale OU page) ───────────────────────
// Rend le formulaire d'édition / création d'une action dans `host`. Ne touche
// pas au chrome (titre / boutons save / fermeture) — c'est l'hôte qui décide
// quoi faire après `save()`. Permet à `pages.action` (la page d'édition)
// et `showEditActionModal` (la modale) de partager exactement le même rendu
// et la même logique.
export function renderActionEditor(
  host: HTMLElement,
  action: ActionEditData,
  options?: ShowEditActionModalOptions,
): RenderActionEditorApi {
  const opts = options || {};
  const users = opts.users || [];
  const statuses = opts.statuses || [];
  const projectsData = opts.projects || [];
  const onSaved = opts.onSaved;
  const prefill = opts.prefill || {};
  const isNew = !action.id;
  const a: ActionEditData = { ...action, ...prefill };
  // Blocs de liaison repliés à l'ouverture (option page — cf. collapseLinkSections).
  const collapseSections = !!opts.collapseLinkSections;

  const individuals = users.filter((u) => !u.is_team);
  // Filtre les statuts système (ex. « Fusionnée ») : posés par l'application,
  // ils ne doivent jamais être assignés manuellement depuis l'éditeur d'action
  // (cf. user 2026-05-21).
  const sortedStatuses = [...statuses]
    .filter((s) => !((s as { is_system?: boolean }).is_system === true))
    .sort((x, y) => x.position - y.position);
  // Statut par défaut : logique pure extraite (cf. domain/action-form).
  const defaultStatusId = computeDefaultStatusId(statuses);
  const effectiveStatusId = a.status_id ?? defaultStatusId;
  const prog = a.progress || 0;

  // ── Auto-note infrastructure (RefactoUX/Actions 2026-05-20) ───────────────
  // À chaque modification d'un champ, on suggère une "Nouvelle note" décrivant
  // ce qui a changé. La textarea est repeuplée tant que l'utilisateur n'a pas
  // tapé dedans (`_userEditedNote`). Au save, si la suggestion auto n'a pas
  // été reprise/éditée, on anime sans sauvegarder ; un 2e clic enregistre.
  //
  // Désactivé pour les nouvelles actions (isNew) : tous les champs sont "neufs"
  // et un "Modification du titre, …" sur une création n'a pas de sens.
  const _autoNoteEnabled = !isNew;
  // État initial figé à l'ouverture (defaults + Sets, priorité repliée sur le barème 0..4 — cf. domain/action-form). Pilote aussi le `selected` du sélecteur de priorité.
  const _originalSnapshot = buildOriginalSnapshot(a, defaultStatusId);
  let _userEditedNote = false;
  let _autoNoteWarned = false;

  // ── État "modifications utilisateur" (RefactoUX/Actions 2026-05-20) ─────
  // À l'ouverture : seul "Fermer" (primary, à la place de "Enregistrer") est
  // visible — pas de boutons d'annulation/sauvegarde inutiles si on consulte
  // sans toucher. Dès la 1ère modif (champ ou textarea), bascule sur le trio
  // Annuler / Enregistrer / Enreg sans Notif. Le flip est one-way (pas de
  // retour arrière) — éviter le yo-yo si l'utilisateur défait sa modif.
  let _hasUserChanges = !!opts.startDirty;
  const _updateCtaVisibility = (): void => {
    const closeBtn = document.getElementById('ea-btn-close');
    const cancelBtn = document.getElementById('ea-btn-cancel');
    const saveGroup = document.getElementById('ea-btn-save-group');
    if (closeBtn) closeBtn.style.display = _hasUserChanges ? 'none' : '';
    if (cancelBtn) cancelBtn.style.display = _hasUserChanges ? '' : 'none';
    if (saveGroup) saveGroup.style.display = _hasUserChanges ? 'inline-flex' : 'none';
  };
  const _markUserChange = (): void => {
    if (_hasUserChanges) return;
    _hasUserChanges = true;
    _updateCtaVisibility();
  };

  // Builders d'<option> délégués au module frère editor-fields (fragments purs).
  const approverOpts = buildApproverOptions(individuals, a.approver_id);
  const ownerOpts = buildOwnerOptions(users, a.owner_id);
  const canToggleConf = isNew || a.can_toggle_confidential === true;
  const statusOpts = buildStatusOptions(sortedStatuses, effectiveStatusId);
  // Origines (risques source) : rendu (helpers sévérité/couleur/tronquage inclus)
  // délégué au module frère editor-fields (fragment pur).
  const originsHtml = renderOriginsHtml(a.origins);
  // À la création (isNew), on affiche "—" parce que l'ID n'est attribué qu'après
  // l'INSERT côté backend. À l'édition, on affiche "#${a.id}".
  const codeDefault = isNew ? '—' : `#${a.id}`;

  // Puces/bloc RACI : rendu délégué au module frère editor-fields (fragment pur,
  // `users` threadé explicitement).
  const raciBlock = (fieldId: string, selectedIds: number[] | undefined): string =>
    renderRaciBlock(fieldId, selectedIds, users);

  const _pendingNotes: string[] = [];

  // ── Layout ────────────────────────────────────────────────────────────────
  const grpStyle =
    'padding:8px 10px;background:var(--bg2);border-radius:var(--radius);border:1px solid var(--border)';
  const grpTitle = (lbl: string): string =>
    `<div style="font-size:10px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px">${lbl}</div>`;
  // Bloc repliable (Projets / Comités / Organisation) : titre cliquable avec
  // triangle ▾, corps masquable, et résumé des valeurs sélectionnées affiché
  // sous le titre quand le bloc est replié (rempli par le wiring au collapse).
  const collapsibleBlock = (
    key: string,
    titleLabel: string,
    bodyHtml: string,
    collapsed = false,
  ): string => `
    <div style="${grpStyle}">
      <button type="button" class="ea-collapse-toggle" data-collapse="${key}" aria-expanded="${collapsed ? 'false' : 'true'}"
        style="display:flex;align-items:center;gap:6px;background:transparent;border:none;cursor:pointer;padding:0;width:100%;text-align:left">
        <span class="ea-collapse-caret" style="font-size:9px;color:var(--text3);transition:transform .15s ease;flex-shrink:0${collapsed ? ';transform:rotate(-90deg)' : ''}">▾</span>
        <span style="font-size:10px;font-weight:600;color:var(--text3);text-transform:uppercase;letter-spacing:.06em">${titleLabel}</span>
      </button>
      <div class="ea-collapse-summary" data-summary="${key}" style="display:${collapsed ? 'flex' : 'none'};flex-wrap:wrap;gap:4px;align-items:center;margin:4px 0 0 15px"></div>
      <div class="ea-collapse-body" data-body="${key}" style="margin-top:6px${collapsed ? ';display:none' : ''}">${bodyHtml}</div>
    </div>`;

  // ── Layout (RefactoUX/Actions — 2026-05-20) ───────────────────────────────
  // Le formulaire occupe TOUTE la hauteur de son host (#modal-body en modal,
  // wrapper de hauteur calculée en page) avec 3 zones :
  //   1) sticky top    : Code | Titre | Priorité
  //   2) middle (3 cols égales) : RACI | Projets/Comités | Notes (col 3 sur
  //      toute la hauteur). Row 1 de middle = Date butoir | Utilisée dans
  //      risques (sur cols 1+2, Notes spannant rows 1-2).
  //   3) sticky bottom (3 cols égales) :
  //      col 1 = Date fin révisée
  //      col 2 = État (row 1) + Avancement (row 2) empilés
  //      col 3 = Saisie note + "+" (row 1) puis Annuler + Enregistrer▾ (row 2)
  //
  // L'origine (entité Origin) a été décommissionnée le 24/05/2026 :
  // seul `usedInRisks` (références à l'action depuis des risques) reste,
  // dans la cellule (1,2) de middle.
  //
  // Les CTA Annuler / Enregistrer dans la sticky-bottom sont la source de
  // vérité — le modal-footer est masqué par le wrapper et le header de
  // page-action ne porte plus que le titre + breadcrumb.
  const body = `<div id="ea-editor-root" style="display:flex;flex-direction:column;height:100%;min-height:520px;gap:0">

    <!-- ZONE 1 : bande sticky (breadcrumb + Code/Titre/Priorité/Confidentiel),
         puis le bloc Livrable attendu + Lien. Cf. renderEditorTopZone. -->
    ${renderEditorTopZone(
      a,
      { codeDefault, canToggleConf, priority: _originalSnapshot.priority },
      opts.stickyTopPrefixHtml || '',
    )}

    <!-- ZONE 2 : Middle (3 cols égales × 2 rows). Notes spanne grid-row:1/-1 sur col 3. -->
    <div class="ea-middle" style="flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr 1fr;grid-template-rows:auto 1fr;column-gap:10px;row-gap:10px;padding:10px 0">

      <!-- Row 1 / col 1 : Date butoir initiale -->
      <div class="ea-field-deadline" style="grid-column:1;grid-row:1;${grpStyle}">
        ${grpTitle(esc(t('actionModal.field.deadline')) + ` <span style="color:var(--warn);text-transform:none;font-weight:400;letter-spacing:0">${esc(t('actionModal.field.deadlineNote'))}</span>`)}
        <input type="date" id="ea-deadline" value="${esc(a.deadline || '')}" style="width:100%">
      </div>

      <!-- Row 1 / col 2 : Utilisée dans risques (read-only) -->
      <div class="ea-block-risks" style="grid-column:2;grid-row:1;${grpStyle}">
        ${grpTitle(esc(t('actionModal.field.usedInRisks')))}
        <div>${originsHtml}</div>
      </div>

      <!-- Row 2 / col 1 : RACI (scrollable en interne si dépassement) -->
      <div class="ea-block-raci" style="grid-column:1;grid-row:2;${grpStyle};overflow-y:auto;min-height:0">
        ${grpTitle(esc(t('actionModal.section.raci')) + ' <button type="button" id="ea-raci-help" class="help-tip" aria-label="' + esc(t('actionModal.raci.fullHelpAria')) + '" title="' + esc(t('actionModal.raci.fullHelpAria')) + '">?</button>')}
        <div style="font-size:12px;color:var(--text2);margin-bottom:2px">${esc(t('actionModal.raci.r'))}</div>
        ${raciBlock('performers', a.performers)}
        <div style="margin-top:8px;margin-bottom:8px">
          <label style="font-size:12px;color:var(--text2)">${esc(t('actionModal.raci.a'))}</label>
          <!-- Picker recherche (cf. user 2026-05-21) : on garde le <select>
               caché comme état (le save lit #ea-approver.value), et on
               affiche un bouton qui ouvre une combobox avec champ de
               recherche au clic. -->
          <select id="ea-approver" style="display:none">${approverOpts}</select>
          <button type="button" id="ea-approver-btn"
                  style="width:100%;margin-top:3px;padding:6px 10px;background:#fff;
                         color:var(--text);border:1px solid var(--border2);
                         border-radius:var(--radius);cursor:pointer;font-size:13px;
                         text-align:left;display:flex;align-items:center;justify-content:space-between">
            <span id="ea-approver-label">${
              a.approver_id
                ? esc(userLabel(users.find((u) => u.id === a.approver_id) ?? null, false))
                : esc(t('pageActionsMerge.approver.choose'))
            }</span>
            <span style="color:var(--text3)">▾</span>
          </button>
        </div>
        <div style="font-size:12px;color:var(--text2);margin-top:8px;margin-bottom:2px">${esc(t('actionModal.raci.c'))}</div>
        ${raciBlock('consulted', a.consulted)}
        <div style="font-size:12px;color:var(--text2);margin-top:8px;margin-bottom:2px">${esc(t('actionModal.raci.i'))}</div>
        ${raciBlock('informed', a.informed)}
        <p style="font-size:10px;color:var(--text3);margin:6px 0 0">${esc(t('actionModal.raci.help'))}</p>

        <!-- Owner (sous le RACI) : le prochain à agir, choisi parmi A∪R. -->
        <div style="margin-top:10px;border-top:1px solid var(--border);padding-top:8px">
          <label style="font-size:12px;color:var(--text2)">${esc(t('actionModal.field.owner'))}</label>
          <select id="ea-owner" style="display:none">${ownerOpts}</select>
          <button type="button" id="ea-owner-btn"
                  style="width:100%;margin-top:3px;padding:6px 10px;background:#fff;
                         color:var(--text);border:1px solid var(--border2);
                         border-radius:var(--radius);cursor:pointer;font-size:13px;
                         text-align:left;display:flex;align-items:center;justify-content:space-between">
            <span id="ea-owner-label">${
              a.owner_id
                ? esc(userLabel(users.find((u) => u.id === a.owner_id) ?? null, false))
                : esc(t('actionModal.owner.choose'))
            }</span>
            <span style="color:var(--text3)">▾</span>
          </button>
          <p style="font-size:10px;color:var(--text3);margin:4px 0 0">${esc(t('actionModal.owner.hint'))}</p>
        </div>

        <!-- Qui voit l'action : survol → liste exhaustive (rassure sur l'accès). -->
        <div class="ea-viewers" style="margin-top:10px;border-top:1px solid var(--border);padding-top:8px;position:relative">
          <span id="ea-viewers-trigger" style="font-size:12px;color:var(--text2);cursor:help;text-decoration:underline dotted">${esc(t('actionModal.viewers.title'))}</span>
          <div id="ea-viewers-panel" style="display:none;position:absolute;z-index:60;left:0;top:calc(100% + 4px);background:var(--bg2);border:1px solid var(--border2);border-radius:var(--radius);box-shadow:0 6px 18px rgba(0,0,0,.14);padding:8px;min-width:230px;max-width:320px;max-height:260px;overflow:auto;font-size:12px"></div>
        </div>
      </div>

      <!-- Row 2 / col 2 : Projets + Comités + Organisation empilés, repliables -->
      <div class="ea-block-projcom" style="grid-column:2;grid-row:2;display:flex;flex-direction:column;gap:10px;min-height:0;overflow-y:auto">
        ${collapsibleBlock(
          'projects',
          esc(t('actionModal.section.projects', { plural: t('vocabulary.project_plural') })),
          `<input type="text" id="ea-proj-search" placeholder="${esc(t('actionModal.projects.searchPlaceholder'))}" autocomplete="off"
            style="width:100%;font-size:11px;padding:4px 8px;border:1px solid var(--border);border-radius:var(--radius);margin-bottom:5px">
          <div id="ea-projects-picker" style="max-height:220px;overflow-y:auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:3px 10px">
            <span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t('actionModal.projects.loading'))}</span>
          </div>`,
          collapseSections,
        )}
        ${collapsibleBlock(
          'committees',
          esc(t('actionModal.section.committees', { plural: t('vocabulary.committee_plural') })),
          `<div id="ea-committees-picker" style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:3px 10px;min-height:24px">
            <span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t('actionModal.committees.loading'))}</span>
          </div>`,
          collapseSections,
        )}
        ${collapsibleBlock(
          'structures',
          esc(t('actionModal.section.organisation')),
          `<input type="text" id="ea-struct-search" placeholder="${esc(t('actionModal.structures.searchPlaceholder'))}" autocomplete="off"
            style="width:100%;font-size:11px;padding:4px 8px;border:1px solid var(--border);border-radius:var(--radius);margin-bottom:5px">
          <div id="ea-structures-picker" style="max-height:220px;overflow-y:auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:3px 10px">
            <span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t('actionModal.structures.loading'))}</span>
          </div>`,
          collapseSections,
        )}
        ${collapsibleBlock(
          'phases',
          esc(t('actionModal.section.actionPlans')),
          `<input type="text" id="ea-phase-search" placeholder="${esc(t('actionModal.phases.searchPlaceholder'))}" autocomplete="off"
            style="width:100%;font-size:11px;padding:4px 8px;border:1px solid var(--border);border-radius:var(--radius);margin-bottom:5px">
          <div id="ea-phases-picker" style="max-height:220px;overflow-y:auto;display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:3px 10px">
            <span style="grid-column:1 / -1;color:var(--text3);font-size:11px">${esc(t('actionModal.phases.loading'))}</span>
          </div>`,
          collapseSections,
        )}
      </div>

      <!-- Col 3 (rows 1-2) : Notes — pleine hauteur du middle -->
      <div class="ea-block-notes" style="grid-column:3;grid-row:1 / -1;${grpStyle};display:flex;flex-direction:column;min-height:0">
        ${grpTitle(esc(t('actionModal.section.notes')) + (!isNew ? ` <span id="notes-badge" style="font-size:10px;background:var(--bg3);padding:1px 5px;border-radius:8px;font-weight:400"></span>` : ''))}
        <div id="notes-list" style="flex:1;overflow-y:auto;min-height:0"></div>
      </div>
    </div>

    <!-- ZONE 3 : Sticky BOTTOM (3 cols égales × 2 rows).
         Col 1 = Date fin révisée
         Col 2 = État + Avancement empilés sur row 1 (Avancement remonté,
                 row 2 libre — pas besoin d'aligner sur les CTA).
         Col 3 = row 1 : Nouvelle note (label + textarea + "+")
                 row 2 : Annuler + Enregistrer▾
         position:sticky bottom:0 garantit que la zone reste visible quel que
         soit le scroll du host (modal-body ou wrapper page). -->
    <div class="ea-sticky-bottom" style="position:sticky;bottom:0;z-index:5;flex-shrink:0;display:grid;grid-template-columns:1fr 1fr 1fr;grid-template-rows:auto auto;column-gap:10px;row-gap:8px;padding:10px 0;background:var(--bg);border-top:1px solid var(--border)">

      <!-- Row 1 / col 1 : Date fin révisée -->
      <div class="ea-field-revised" style="grid-row:1;grid-column:1">
        <label style="font-size:11px;color:var(--text3)">${esc(t('actionModal.field.deadlineRevised'))}</label>
        <input type="date" id="ea-deadline-revised" data-testid="action-deadline-revised" value="${esc(a.deadline_revised || '')}" style="width:100%;margin-top:2px">
      </div>

      <!-- Row 1 / col 2 : État + Avancement empilés -->
      <div class="ea-field-status" style="grid-row:1;grid-column:2;display:flex;flex-direction:column;gap:6px">
        <div>
          <label style="font-size:11px;color:var(--text3)">${esc(t('actionModal.field.status'))}</label>
          <select id="ea-status" data-testid="action-status" style="width:100%;margin-top:2px">${statusOpts}</select>
        </div>
        <div>
          <label style="font-size:11px;color:var(--text3)">${esc(t('actionModal.field.progress'))}</label>
          <div style="display:flex;align-items:center;gap:8px;margin-top:2px">
            <input type="range" id="ea-progress" min="0" max="100" value="${prog}" style="flex:1">
            <span id="ea-progress-val" style="font-size:12px;min-width:32px">${prog}%</span>
          </div>
        </div>
      </div>

      <!-- Col 3 : Nouvelle note (label + textarea 2 lignes + bouton "+")
           PUIS CTAs (Fermer / Annuler + Enregistrer▾) directement en dessous
           pour les rapprocher visuellement de la zone Nouvelle note (mai 2026). -->
      <div class="ea-block-newnote" style="grid-row:1 / span 2;grid-column:3;display:flex;flex-direction:column;gap:8px">
        <!-- Nouvelle note (label + textarea + "+") -->
        <div>
          <label style="font-size:11px;color:var(--text3)">${esc(t('actionModal.field.newNote'))}</label>
          <div style="display:flex;gap:6px;align-items:flex-start;margin-top:2px">
            <textarea id="new-note-text" rows="2"
              placeholder="${esc(t('actionModal.notes.placeholder'))}"
              autocomplete="off"
              style="flex:1;font-size:13px;padding:6px 10px;border:1px solid var(--border2);border-radius:var(--radius);resize:vertical;font-family:inherit">${isNew ? esc(t('actionModal.notes.creationDefault')) : ''}</textarea>
            <!-- Bouton "+" : 1 ligne de haut (gabarit identique au "+" du
                 header post-it, ~32px). Reste primary (vert). -->
            <button id="btn-add-note" class="btn btn-primary btn-sm"
              title="${esc(t('actionModal.notes.btnAdd'))}" aria-label="${esc(t('actionModal.notes.btnAdd'))}"
              style="padding:0 10px;height:32px;white-space:nowrap;font-weight:700;font-size:16px;line-height:1;flex-shrink:0">+</button>
          </div>
        </div>

        ${renderEditorCtas()}
      </div>
    </div>
  </div>`;

  // eslint-disable-next-line no-restricted-syntax -- body construit ci-dessus avec esc() sur toutes les valeurs variables (pattern hérité de openWideModal).
  host.innerHTML = body;

  // Cast vers le type strict de window.d.ts (qui n'accepte pas trigram null).
  // Le mismatch est cosmétique : à l'usage, le picker accepte tout ce qui a un
  // id et un name. À fusionner avec ActionUser quand window.d.ts sera retiré (S6+).
  window._actionModalUsers = users as Array<{ id: number; trigram: string; name: string }>;

  // Mode US (mai 2026) : seule la case A du RACI est éditable.
  // R (performers), C (consulted), I (informed) sont verrouillés visuellement
  // et déclenchent le bandeau d'upsell au clic.
  if (window.isSoloUser?.()) {
    for (const fieldId of ['performers', 'consulted', 'informed']) {
      const block = document.getElementById('raci-' + fieldId);
      if (block && window.markLocked) {
        window.markLocked(block, 'solo');
      }
    }
  }

  // ── Lecture brute du formulaire : source UNIQUE pour le diff ET le save ───
  // Toute la logique de normalisation/diff/payload vit dans domain/action-form ;
  // ici on se contente de collecter les valeurs depuis le DOM et les Sets vivants.
  // Sélections vivantes des pickers de liaison (posées par wireLinkPickers dans
  // le setTimeout ci-dessous, toujours avant tout save).
  let _links: LinkPickersSelection | null = null;
  const _readFormRawInput = (): ActionFormRawInput => {
    const val = (id: string): string =>
      (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value || '';
    const structIds = Array.from(_links?.structureIds ?? []);
    return {
      text: (document.getElementById('ea-text') as HTMLInputElement | null)?.value ?? '',
      deadlineValue: val('ea-deadline'),
      deadlineRevisedValue: val('ea-deadline-revised'),
      statusValue: val('ea-status'),
      progressValue: val('ea-progress'),
      priorityValue: val('ea-priority'),
      expectedDeliverableValue: val('ea-deliverable'),
      externalLinkValue: val('ea-external-link'),
      approverValue: val('ea-approver'),
      ownerValue: val('ea-owner'),
      confidentialChecked:
        (document.getElementById('ea-confidential') as HTMLInputElement | null)?.checked ?? false,
      performers: _getRaciIds('performers'),
      consulted: _getRaciIds('consulted'),
      informed: _getRaciIds('informed'),
      projectIds: Array.from(_links?.projectIds ?? []),
      committeeIds: Array.from(
        document.querySelectorAll<HTMLInputElement>('.ea-committee-chk:checked'),
      ).map((c) => parseInt(c.value)),
      structureIds: structIds,
      pickedEscalatedIds: Array.from(_links?.escalatedStructureIds ?? []),
      phaseIds: Array.from(_links?.phaseIds ?? []),
    };
  };
  // Auto-note : diff snapshot ↔ payload courant (libellés FR) — cf. domain.
  const _changedFieldLabels = (): string[] =>
    changedActionFields(_originalSnapshot, buildActionPayload(_readFormRawInput()));
  const _flashNoteZone = (): void => {
    const ta = document.getElementById('new-note-text');
    if (!ta) return;
    ta.classList.remove('ea-auto-note-flash');
    // Force reflow → animation rejouable même si déclenchée 2 fois de suite.
    void (ta as HTMLElement).offsetWidth;
    ta.classList.add('ea-auto-note-flash');
  };
  const _recomputeAutoNote = (): void => {
    if (!_autoNoteEnabled) return;
    if (_userEditedNote) return;
    const ta = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
    if (!ta) return;
    const labels = _changedFieldLabels();
    const newText = labels.length === 0 ? '' : 'Modification ' + labels.join(', ');
    if (ta.value === newText) return;
    ta.value = newText;
    // Tout nouveau contenu auto remet le garde-fou save à zéro : il faudra
    // à nouveau un clic explicite pour passer outre.
    _autoNoteWarned = false;
    if (newText) _flashNoteZone();
  };

  const _doSaveAction = async (notify = true): Promise<boolean> => {
    // Le champ Code est readonly depuis avril 2026 (affichage de a.id ou "—"
    // en création) et n'est plus envoyé au backend. La forme du payload et les
    // garde-fous de validation vivent désormais dans domain/action-form.
    const data = buildActionPayload(_readFormRawInput());

    // Owner devenu invalide : s'il n'est plus dans A∪R (R/A modifiés depuis sa
    // désignation), on demande confirmation. Confirmé → owner mis à NULL ;
    // annulé → on interrompt le save pour laisser l'utilisateur ajuster.
    if (data.owner_id != null) {
      const cand = new Set<number>(data.performers);
      if (data.approver_id != null) cand.add(data.approver_id);
      if (!cand.has(data.owner_id)) {
        const ok = await confirmStackModal({ message: t('actionModal.owner.removedConfirm') });
        if (!ok) return false;
        data.owner_id = null;
        const sel = document.getElementById('ea-owner') as HTMLSelectElement | null;
        const lbl = document.getElementById('ea-owner-label');
        if (sel) sel.value = '';
        if (lbl) lbl.textContent = t('actionModal.owner.choose');
      }
    }

    // Garde-fous (création sans projet, modif de la date butoir initiale) :
    // décision pure ; ici on n'orchestre que les confirmations utilisateur.
    // Mai 2026 : confirmStackModal stacké au-dessus de la modale parent (P2 #13).
    const confirms = requiredSaveConfirmations({
      isNew,
      projectIdsCount: data.project_ids.length,
      oldDeadline: a.deadline || null,
      newDeadline: data.deadline || null,
    });
    for (const c of confirms) {
      const msgKey =
        c === 'noProject' ? 'actionModal.confirmNoProject' : 'actionModal.confirmDeadlineChange';
      const ok = await confirmStackModal({ message: t(msgKey) });
      if (!ok) return false;
    }

    // Save guard (RefactoUX/Actions 2026-05-20) : si une auto-note non relue est
    // dans la textarea, on anime et on annule ce 1er clic ; le 2e passe.
    const _noteGuardEl = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
    if (
      shouldBlockForAutoNote({
        autoNoteEnabled: _autoNoteEnabled,
        userEditedNote: _userEditedNote,
        autoNoteWarned: _autoNoteWarned,
        autoNoteText: _noteGuardEl?.value || '',
      })
    ) {
      _autoNoteWarned = true;
      _flashNoteZone();
      return false;
    }

    // RefactoUX/Actions 2026-05-20 : le contenu de la textarea "Nouvelle note"
    // est TOUJOURS enregistré comme une note à la sauvegarde, qu'il vienne de
    // l'auto-fill, du pre-fill "Création de l'action" (isNew), ou de la saisie
    // utilisateur. Le bouton "+" reste utile pour ajouter une note SANS sortir
    // de l'éditeur (cas d'usage : annoter en cours d'édition).
    const ta = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
    const autoNoteText = (ta?.value || '').trim();

    if (isNew) {
      if (autoNoteText) _pendingNotes.push(autoNoteText);
      if (ta) ta.value = '';
      onSaved?.(data, _pendingNotes, notify);
      return true;
    }
    if (!action.id) return false;
    try {
      const updated = await api.updateAction(action.id, data, { notify });
      // Auto-save de la note "Nouvelle note" si non vide (geste implicite,
      // évite à l'utilisateur d'avoir à cliquer "+" avant Enregistrer).
      if (autoNoteText) {
        try {
          await api.addActionNote(action.id, {
            comment: autoNoteText,
            user_id: currentUserId(),
          });
        } catch (e) {
          safeWarn('addActionNote on save', e);
        }
      }
      // Avril 2026 : marque l'action comme "vue" par l'utilisateur courant
      // après sa propre modification, sinon le badge "Modifié" apparaîtrait
      // sur sa propre action au prochain refresh.
      try {
        await api.markActionSeen(action.id);
      } catch {
        /* non critique */
      }
      if (ta) ta.value = '';
      toast(t('actionModal.toast.updated'));
      onSaved?.(updated);
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('common.error');
      toast(msg, 'error');
      return false;
    }
  };

  setTimeout(() => {
    // Passe `users` à _wireActionModal pour que le picker RACI puisse l'utiliser.
    _wireActionModal(users);
    if (!isNew && action.id) {
      _wireAddNoteButton(action.id);
      _loadNotes(action.id);
    } else {
      _wireNewActionNotes(_pendingNotes);
    }

    // ── Boutons sticky-bottom (Fermer / Annuler / Enregistrer + split) ──
    // Source de vérité unique : le modal-footer est masqué côté wrapper et
    // page-action n'affiche plus de boutons dans son header. "Fermer" est
    // l'état initial (avant 1ère modif) — il déclenche `onCancel` puisque
    // sémantiquement c'est une sortie sans sauvegarde.
    const closeBtn = document.getElementById('ea-btn-close');
    closeBtn?.addEventListener('click', () => opts.onCancel?.());
    const cancelBtn = document.getElementById('ea-btn-cancel');
    cancelBtn?.addEventListener('click', () => opts.onCancel?.());

    const saveBtn = document.getElementById('ea-btn-save') as HTMLButtonElement | null;
    saveBtn?.addEventListener('click', () => {
      void api.withButton(saveBtn, async () => {
        await _doSaveAction(true);
      });
    });

    // Split-menu : "▾" toggle le menu "Enreg sans Notif". Fermeture sur clic
    // extérieur ou ESC. aria-expanded suit l'état pour les lecteurs d'écran.
    const menuBtn = document.getElementById('ea-btn-save-menu') as HTMLButtonElement | null;
    const menu = document.getElementById('ea-save-menu');
    const closeMenu = (): void => {
      if (menu) menu.style.display = 'none';
      menuBtn?.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onOutsideMenu, true);
      document.removeEventListener('keydown', onEscMenu, true);
    };
    const onOutsideMenu = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menu?.contains(target) || menuBtn?.contains(target)) return;
      closeMenu();
    };
    const onEscMenu = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeMenu();
    };
    menuBtn?.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      if (!menu) return;
      const willOpen = menu.style.display === 'none';
      menu.style.display = willOpen ? 'block' : 'none';
      menuBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) {
        // Listeners de fermeture attachés au prochain tick pour ignorer le
        // clic d'ouverture lui-même.
        setTimeout(() => {
          document.addEventListener('mousedown', onOutsideMenu, true);
          document.addEventListener('keydown', onEscMenu, true);
        }, 0);
      } else {
        document.removeEventListener('mousedown', onOutsideMenu, true);
        document.removeEventListener('keydown', onEscMenu, true);
      }
    });

    const saveNoNotifyBtn = document.getElementById(
      'ea-btn-save-no-notify',
    ) as HTMLButtonElement | null;
    saveNoNotifyBtn?.addEventListener('click', () => {
      closeMenu();
      if (!saveBtn) return;
      void api.withButton(saveBtn, async () => {
        await _doSaveAction(false);
      });
    });

    // ── Listeners "modifications" : tout changement déclenche à la fois
    //    _markUserChange (bascule CTA Fermer → Annuler/Enregistrer) et
    //    _recomputeAutoNote (suggestion de note auto, edit seulement).
    const onFieldChange = (): void => {
      _markUserChange();
      _recomputeAutoNote();
    };
    document.getElementById('ea-text')?.addEventListener('input', onFieldChange);
    document.getElementById('ea-deadline')?.addEventListener('change', onFieldChange);
    document.getElementById('ea-deadline-revised')?.addEventListener('change', onFieldChange);
    document.getElementById('ea-status')?.addEventListener('change', onFieldChange);
    document.getElementById('ea-priority')?.addEventListener('change', onFieldChange);
    document.getElementById('ea-deliverable')?.addEventListener('input', onFieldChange);
    wireExternalLinkField(onFieldChange);
    document.getElementById('ea-progress')?.addEventListener('input', onFieldChange);
    document.getElementById('ea-approver')?.addEventListener('change', onFieldChange);
    // Comités : délégation sur le picker pour intercepter chaque toggle.
    // (Projets / structures / phases : câblés par wireLinkPickers, plus bas.)
    document.getElementById('ea-committees-picker')?.addEventListener('change', onFieldChange);

    // ── Owner + Confidentialité + « Qui voit l'action » (juin 2026) ──────────
    // « Qui voit » : sur survol, liste exhaustive des accédants.
    //  - Confidentiel : calculé côté client (RACI vivante : personnes + équipes)
    //    → recalcul instantané quand on bascule le toggle (rassure).
    //  - Public : liste exhaustive via le backend (action déjà enregistrée).
    const _recomputeViewers = (): void => {
      const panel = document.getElementById('ea-viewers-panel');
      if (!panel) return;
      const conf =
        (document.getElementById('ea-confidential') as HTMLInputElement | null)?.checked ?? false;
      if (conf) {
        const ids = new Set<number>([
          ..._getRaciIds('performers'),
          ..._getRaciIds('consulted'),
          ..._getRaciIds('informed'),
        ]);
        const ap = parseInt(
          (document.getElementById('ea-approver') as HTMLSelectElement | null)?.value || '',
          10,
        );
        if (ap) ids.add(ap);
        const entities = users.filter((u) => ids.has(u.id));
        const body = entities.length
          ? entities
              .map((u) => `<div>${esc(u.is_team ? `[équipe] ${u.name}` : u.name)}</div>`)
              .join('')
          : `<div style="color:var(--text3)">${esc(t('actionModal.viewers.none'))}</div>`;
        panel.innerHTML =
          `<div style="font-weight:600;margin-bottom:4px">${esc(t('actionModal.viewers.confidential'))}</div>` +
          body;
        return;
      }
      if (!a.id) {
        panel.innerHTML = `<div style="color:var(--text3)">${esc(t('actionModal.viewers.saveFirst'))}</div>`;
        return;
      }
      panel.innerHTML = `<div style="color:var(--text3)">${esc(t('common.loading'))}</div>`;
      void (async () => {
        try {
          const res = (await api.getActionViewers(a.id as number)) as {
            viewers?: Array<{ id: number; name: string; is_team?: boolean }>;
          };
          const vs = res.viewers || [];
          panel.innerHTML = vs.length
            ? vs.map((v) => `<div>${esc(v.is_team ? `[équipe] ${v.name}` : v.name)}</div>`).join('')
            : `<div style="color:var(--text3)">${esc(t('actionModal.viewers.none'))}</div>`;
        } catch {
          panel.innerHTML = `<div style="color:var(--danger)">${esc(t('common.error'))}</div>`;
        }
      })();
    };
    const viewersTrigger = document.getElementById('ea-viewers-trigger');
    const viewersWrap = viewersTrigger?.closest('.ea-viewers');
    const viewersPanel = document.getElementById('ea-viewers-panel');
    if (viewersTrigger && viewersPanel) {
      viewersTrigger.addEventListener('mouseenter', () => {
        _recomputeViewers();
        viewersPanel.style.display = 'block';
      });
      viewersWrap?.addEventListener('mouseleave', () => {
        viewersPanel.style.display = 'none';
      });
    }

    // Owner picker : candidats = A (approver) ∪ R (performers), lus en direct.
    const _ownerCandidateIds = (): Set<number> => {
      const ids = new Set<number>(_getRaciIds('performers'));
      const ap = parseInt(
        (document.getElementById('ea-approver') as HTMLSelectElement | null)?.value || '',
        10,
      );
      if (ap) ids.add(ap);
      return ids;
    };
    const _setOwner = (id: number | null): void => {
      const sel = document.getElementById('ea-owner') as HTMLSelectElement | null;
      const lbl = document.getElementById('ea-owner-label');
      if (sel) sel.value = id ? String(id) : '';
      if (lbl) {
        const u = id ? (users.find((x) => x.id === id) ?? null) : null;
        lbl.textContent = u ? userLabel(u, false) : t('actionModal.owner.choose');
      }
      onFieldChange();
    };
    const _showOwnerDropdown = (anchor: HTMLElement): void => {
      document.querySelectorAll('.ea-owner-dd').forEach((el) => el.remove());
      const cand = _ownerCandidateIds();
      const list = users.filter((u) => cand.has(u.id));
      const dd = document.createElement('div');
      dd.className = 'ea-owner-dd';
      const r = anchor.getBoundingClientRect();
      dd.style.cssText =
        'position:fixed;z-index:1000;background:var(--bg2);border:1px solid var(--border2);' +
        'border-radius:var(--radius);box-shadow:0 8px 24px rgba(0,0,0,.15);max-height:240px;' +
        `overflow:auto;min-width:${Math.round(r.width)}px;left:${Math.round(r.left)}px;top:${Math.round(r.bottom + 4)}px`;
      const rows: string[] = [
        `<div data-owner-pick="" style="padding:6px 10px;cursor:pointer;font-size:12px;color:var(--text3)">${esc(t('actionModal.owner.none'))}</div>`,
      ];
      if (list.length === 0) {
        rows.push(
          `<div style="padding:10px;color:var(--text3);font-size:12px">${esc(t('actionModal.owner.noCandidates'))}</div>`,
        );
      } else {
        for (const u of list) {
          rows.push(
            `<div data-owner-pick="${u.id}" style="padding:6px 10px;cursor:pointer;font-size:12px">${esc(u.is_team ? `[équipe] ${u.name}` : u.name)}</div>`,
          );
        }
      }
      dd.innerHTML = rows.join('');
      document.body.appendChild(dd);
      dd.querySelectorAll<HTMLElement>('[data-owner-pick]').forEach((el) => {
        el.addEventListener('click', () => {
          const v = el.dataset['ownerPick'] || '';
          _setOwner(v ? parseInt(v, 10) : null);
          dd.remove();
        });
      });
      setTimeout(() => {
        const onDoc = (ev: MouseEvent): void => {
          if (!dd.contains(ev.target as Node) && ev.target !== anchor) {
            dd.remove();
            document.removeEventListener('click', onDoc);
          }
        };
        document.addEventListener('click', onDoc);
      }, 0);
    };
    document.getElementById('ea-owner-btn')?.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      _showOwnerDropdown(e.currentTarget as HTMLElement);
    });
    document.getElementById('ea-confidential')?.addEventListener('change', () => {
      onFieldChange();
      _recomputeViewers();
    });

    // RACI chips : pas d'événement natif quand showRaciPicker remplace
    // l'innerHTML de chips-* — MutationObserver sur chacun des 3 conteneurs.
    (['performers', 'consulted', 'informed'] as const).forEach((field) => {
      const chips = document.getElementById('chips-' + field);
      if (!chips) return;
      new MutationObserver(onFieldChange).observe(chips, { childList: true, subtree: true });
    });

    const ta = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
    if (ta) {
      // Saisie utilisateur dans la textarea = à la fois "modification" (bascule
      // CTA) et "édition de la note" (coupe l'auto-fill côté edit-mode).
      ta.addEventListener('input', () => {
        _userEditedNote = true;
        _markUserChange();
      });
      // Le "+" a vidé la textarea → on reprend la main pour proposer la
      // prochaine suggestion (geste explicite de l'utilisateur).
      ta.addEventListener('ea-note-added', () => {
        _userEditedNote = false;
        _autoNoteWarned = false;
        _recomputeAutoNote();
      });
    }

    // ── Pickers de liaison (Projets / Comités / Organisation / Plans) + repli ──
    // Câblage délégué au module frère link-pickers, qui rend les Sets vivants
    // des sélections : `_readFormRawInput` les lit comme source de vérité (le
    // DOM ne voit que les cases non filtrées).
    _links = wireLinkPickers({ action: a, projects: projectsData, onFieldChange });
  }, 0);

  return { save: _doSaveAction };
}

// Wire des handlers du formulaire d'action : range progress, picker origine
// (toggle, recherche, création inline), boutons RACI "+" et "remove".
//
// Top-level (pas closure de showEditActionModal) car appelée via setTimeout
// après openWideModal pour laisser le DOM se construire avant d'attacher
// les listeners.
function _wireActionModal(users: ActionUser[]): void {
  const slider = document.getElementById('ea-progress') as HTMLInputElement | null;
  const label = document.getElementById('ea-progress-val');
  if (slider && label)
    slider.addEventListener('input', () => {
      label.textContent = slider.value + '%';
    });

  // Entité Origin entièrement décommissionnée le 24/05/2026 (modèle +
  // table + endpoints + page admin).

  // Wire RACI "+" buttons — ouvre le dropdown picker ancré sous le bouton.
  document.querySelectorAll<HTMLButtonElement>('.raci-add-btn').forEach((btn) => {
    btn.addEventListener('click', (e: Event) => {
      e.stopPropagation(); // évite que le clic-extérieur du dropdown se déclenche immédiatement
      const field = btn.dataset['raciField'];
      if (field) showRaciPicker(field, btn, users);
    });
  });

  // Mai 2026 : cliquer n'importe où dans la zone R/C/I (en dehors d'une puce
  // existante ou du bouton ✕ de retrait) ouvre aussi le picker — affordance
  // demandée pour ne pas obliger l'utilisateur à viser le petit "+". Le "+"
  // sert toujours d'ancre visuelle de positionnement du dropdown.
  document.querySelectorAll<HTMLElement>('.raci-block').forEach((block) => {
    block.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Ignorer les clics qui ont leur propre handler.
      if (target.closest('.raci-add-btn')) return;
      if (target.closest('[data-remove-raci]')) return;
      const id = block.id; // "raci-performers" / "raci-consulted" / "raci-informed"
      if (!id.startsWith('raci-')) return;
      const field = id.substring(5);
      const anchor = block.querySelector<HTMLElement>('.raci-add-btn') || block;
      showRaciPicker(field, anchor, users);
    });
    // Affordance : curseur "pointer" sur tout le bloc.
    block.style.cursor = 'pointer';
  });

  // Wire "?" RACI : popover explicatif au clic. Texte long avec listes ;
  // un title= natif ne ferait pas l'affaire pour le formatage, on construit
  // donc un overlay stacké léger (z-index `--z-modal-stack`).
  document.getElementById('ea-raci-help')?.addEventListener('click', (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    _showRaciHelpPopover();
  });

  document.querySelectorAll<HTMLButtonElement>('[data-remove-raci]').forEach((btn) => {
    btn.addEventListener('click', () =>
      _removeRaciChip(btn.dataset['field'] || '', parseInt(btn.dataset['removeRaci'] || '0')),
    );
  });

  // Picker Approbateur (cf. user 2026-05-21) : clic sur le bouton → dropdown
  // anchored avec champ de recherche + liste filtrée. Sélection → met à
  // jour le <select> caché #ea-approver et tire un event 'change' pour
  // que le code de save existant (et les listeners onFieldChange) le
  // captent sans modification.
  const approverBtn = document.getElementById('ea-approver-btn');
  if (approverBtn) {
    approverBtn.addEventListener('click', (e: Event) => {
      e.stopPropagation();
      _showApproverDropdown(approverBtn, users);
    });
  }
}

function _showApproverDropdown(anchor: HTMLElement, users: ActionUser[]): void {
  document.querySelectorAll('.ea-approver-dd').forEach((el) => el.remove());

  const selectEl = document.getElementById('ea-approver') as HTMLSelectElement | null;
  const labelEl = document.getElementById('ea-approver-label');
  const currentId = parseInt(selectEl?.value || '') || null;

  let search = '';
  // Filtre actifs uniquement (cf. user 2026-05-21).
  const sorted = [...users.filter((u) => isUserActive(u as never))].sort((a, b) =>
    a.is_team === b.is_team ? a.name.localeCompare(b.name) : a.is_team ? 1 : -1,
  );

  const renderList = (): string => {
    const q = search.toLowerCase();
    const filtered = sorted.filter(
      (u) => !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
    );
    if (filtered.length === 0) {
      return `<div style="padding:12px;color:var(--text3);font-size:12px;text-align:center">${esc(t('actionModal.picker.empty'))}</div>`;
    }
    return filtered
      .map((u) => {
        const sel = u.id === currentId;
        return (
          `<label data-uid-pick="${u.id}" style="display:flex;align-items:center;gap:7px;padding:6px 10px;cursor:pointer;font-size:12px;${sel ? 'background:var(--accent-bg);font-weight:600' : ''}">` +
          `<span style="font-size:10px;color:var(--text3);min-width:32px;display:inline-block">${esc(u.trigram || '')}</span>` +
          `<span>${esc(u.is_team ? `[équipe] ${u.name}` : u.name)}</span>` +
          `</label>`
        );
      })
      .join('');
  };

  const dd = document.createElement('div');
  dd.className = 'ea-approver-dd';
  dd.style.cssText = [
    'position:fixed',
    'z-index:1000',
    'background:var(--bg2)',
    'border:1px solid var(--border2)',
    'border-radius:var(--radius)',
    'box-shadow:0 8px 24px rgba(0,0,0,.15)',
    'min-width:280px',
    'max-height:420px',
    'overflow:hidden',
    'display:flex',
    'flex-direction:column',
  ].join(';');
  dd.innerHTML = `
    <div style="padding:8px;border-bottom:1px solid var(--border)">
      <input type="text" class="search" placeholder="${esc(t('actionModal.picker.searchPlaceholder'))}"
        style="width:100%;font-size:12px;padding:6px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
    </div>
    <div class="pick-list" style="overflow-y:auto;flex:1">${renderList()}</div>
    <div style="padding:8px 10px;border-top:1px solid var(--border);display:flex;justify-content:flex-end">
      <button type="button" class="btn btn-secondary btn-sm picker-clear">${esc(t('pageActionsMerge.approver.clear'))}</button>
    </div>`;
  document.body.appendChild(dd);

  const rect = anchor.getBoundingClientRect();
  const ddW = 280;
  const ddH = 420;
  const margin = 8;
  let left = rect.left;
  if (left + ddW > window.innerWidth - margin) left = window.innerWidth - ddW - margin;
  let top = rect.bottom + 4;
  if (top + ddH > window.innerHeight - margin) {
    top = Math.max(margin, rect.top - ddH - 4);
  }
  dd.style.top = top + 'px';
  dd.style.left = left + 'px';

  const searchInput = dd.querySelector('input.search') as HTMLInputElement;
  const listEl = dd.querySelector('.pick-list') as HTMLElement;
  const clearBtn = dd.querySelector('.picker-clear') as HTMLButtonElement;
  setTimeout(() => searchInput.focus(), 0);

  searchInput.addEventListener('input', () => {
    search = searchInput.value;
    listEl.innerHTML = renderList();
  });

  const commit = (uid: number | null): void => {
    if (selectEl) {
      selectEl.value = uid != null ? String(uid) : '';
      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (labelEl) {
      if (uid == null) {
        labelEl.textContent = t('pageActionsMerge.approver.choose');
      } else {
        const u = users.find((x) => x.id === uid) ?? null;
        labelEl.textContent = userLabel(u, false);
      }
    }
    close();
  };

  listEl.addEventListener('click', (e: Event) => {
    const label = (e.target as HTMLElement | null)?.closest<HTMLElement>('label[data-uid-pick]');
    if (!label) return;
    const uid = parseInt(label.dataset['uidPick'] || '0');
    if (uid > 0) commit(uid);
  });

  clearBtn.addEventListener('click', (e: Event) => {
    e.stopPropagation();
    commit(null);
  });

  const close = (): void => {
    dd.remove();
    document.removeEventListener('mousedown', onDocClick, true);
    document.removeEventListener('keydown', onEsc, true);
  };
  const onDocClick = (e: MouseEvent): void => {
    const target = e.target as Node | null;
    if (!target) return;
    if (dd.contains(target) || anchor.contains(target)) return;
    close();
  };
  const onEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  setTimeout(() => {
    document.addEventListener('mousedown', onDocClick, true);
    document.addEventListener('keydown', onEsc, true);
  }, 0);
}

// Popover d'aide RACI : overlay stacké au-dessus de la modale d'action
// parent. Le HTML provient de la clé i18n `actionModal.raci.fullHelpHtml`,
// fait confiance à son contenu (catalog versionné Git), donc pas d'esc().
function _showRaciHelpPopover(): void {
  const existing = document.getElementById('raci-help-overlay');
  if (existing) {
    existing.remove();
    return;
  }
  const overlay = document.createElement('div');
  overlay.id = 'raci-help-overlay';
  overlay.className = 'modal-overlay modal-stack';
  overlay.innerHTML = `
    <div class="modal" style="max-width:560px;width:100%">
      <div class="modal-header">
        <h2 id="raci-help-title">${esc(t('actionModal.raci.fullHelpTitle'))}</h2>
        <button type="button" class="btn btn-secondary btn-sm" data-raci-help-close aria-label="${esc(t('common.close'))}">✕</button>
      </div>
      <div class="modal-body" style="font-size:13px;line-height:1.55;color:var(--text2)">
        ${t('actionModal.raci.fullHelpHtml')}
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" data-raci-help-close>${esc(t('common.close'))}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const close = (): void => {
    overlay.remove();
    document.removeEventListener('keydown', onKey, true);
  };
  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('[data-raci-help-close]') || e.target === overlay) close();
  });
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  document.addEventListener('keydown', onKey, true);
  setTimeout(
    () => overlay.querySelector<HTMLElement>('[data-raci-help-close].btn-primary')?.focus(),
    0,
  );
}

// ── ESM globals shim ─────────────────────────────────────────────────────
// Expose showEditActionModal sur window pour que les autres modules ESM
// y accèdent comme identifiant nu (page-actions, page-project, page-bascule,
// page-meeting-wizard appellent showEditActionModal(...) en bare ref).
window.showEditActionModal = showEditActionModal;
window.renderActionEditor = renderActionEditor;
(window as unknown as Record<string, unknown>)['showRaciHelpPopover'] = _showRaciHelpPopover;
